/**
 * src/api/bugReportApi.js
 *
 * Bug report API — Azure Functions as backend, GitHub Issues as source of truth.
 *
 * Tenant users submit bugs via the create-bug-issue Azure Function, which
 * creates a GitHub Issue and stores a lightweight reference in bug_refs.
 * Status is synced back via GitHub Actions → sync-bug-status Azure Function.
 */

import { getFunctionUrl } from './functionUrl.js';
import { getSessionToken, azureGet } from './azureClient.js';

/**
 * Submit a new bug report.
 * Calls the create-bug-issue Edge Function which creates a GitHub Issue
 * and stores a reference in bug_refs.
 */
export async function submitBugReport({
  title,
  description,
  session,
  tenant,
  pageUrl,
  pageModule,
  browserInfo,
}) {
  if (!tenant?.id || typeof tenant.id !== 'string' || tenant.id.trim() === '') {
    throw new Error('Invalid tenant ID');
  }

  const environment = detectEnvironment();

  const headers = { 'Content-Type': 'application/json' };

  const token = getSessionToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(getFunctionUrl('create-bug-issue'), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      title: title.trim(),
      description: description || null,
      reporter: session.displayName || session.username,
      tenantName: tenant.name || tenant.slug || null,
      page: pageUrl || null,
      page_module: pageModule || null,
      browser: browserInfo || null,
      environment,
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Failed to submit bug report' }));
    throw new Error(err.error || 'Failed to submit bug report');
  }

  return response.json();
}

/**
 * List bug refs for the current tenant (user-facing).
 * On Azure, calls the bugs-list Azure Function which returns tenant-scoped bugs.
 */
export async function listBugReportsForUser() {
  try {
    const data = await azureGet('/api/bugs-list');
    return data || [];
  } catch (e) {
    console.warn('[bugReportApi] bugs-list failed, falling back to GitHub:', e);
    return (await listGitHubBugIssues()) || [];
  }
}

/**
 * List all bug refs across tenants (super-admin only).
 * On Azure, calls the bugs-list Azure Function (super-admin sees all).
 */
export async function listBugReports() {
  try {
    const data = await azureGet('/api/bugs-list');
    return (data || []).map((r) => ({
      ...r,
      gh_state: r.status === 'deployed' ? 'closed' : 'open',
    }));
  } catch (e) {
    console.warn('[bugReportApi] bugs-list failed, falling back to GitHub:', e);
    return (await listGitHubBugIssues()) || [];
  }
}

const GITHUB_REPO = 'ecurity-msk/seasuite';

/**
 * Fetch all issues directly from GitHub Issues API (paginated).
 *
 * Uses VITE_GITHUB_TOKEN if configured (required for private repos).
 * Falls back gracefully if GitHub is unreachable or the repo is private
 * without a token — callers should treat a null return as "unavailable".
 *
 * Returns an array of normalised issue objects shaped like bug_refs rows,
 * or null if the fetch could not be completed.
 */
export async function listGitHubBugIssues() {
  const token = import.meta.env.VITE_GITHUB_TOKEN;
  const headers = {
    Accept: 'application/vnd.github+json',
    'X-GitHub-Api-Version': '2022-11-28',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    // Paginate through all issues (100 per page) until we get a partial page.
    let page = 1;
    let allIssues = [];
    while (true) {
      const res = await fetch(
        `https://api.github.com/repos/${GITHUB_REPO}/issues?state=all&per_page=100&page=${page}`,
        { headers }
      );
      if (!res.ok) return null; // private repo without token, or rate-limited
      const batch = await res.json();
      // GitHub returns pull requests in the issues endpoint — exclude them.
      const issues = batch.filter((i) => !i.pull_request);
      allIssues = [...allIssues, ...issues];
      if (batch.length < 100) break;
      page++;
    }

    return allIssues.map((issue) => {
      const labels = issue.labels.map((l) => l.name);
      const statusLabel = labels.find((l) => l.startsWith('status:'));
      const envLabel = labels.find((l) => l.startsWith('env:'));
      const tenantLabel = labels.find((l) => l.startsWith('tenant:'));

      const ghStatus = statusLabel?.replace('status:', '') || 'reported';
      const validStatuses = ['reported', 'in-progress', 'in-test', 'deployed'];
      const status =
        issue.state === 'closed'
          ? 'deployed'
          : validStatuses.includes(ghStatus)
            ? ghStatus
            : 'reported';

      return {
        id: `gh-${issue.number}`,
        github_issue_number: issue.number,
        title: issue.title.replace(/^\[[A-Z]+\]\s*/, ''), // strip [ENV] prefix
        gh_state: issue.state, // 'open' | 'closed'
        status,
        environment: envLabel?.replace('env:', '') || 'production',
        tenant_slug: tenantLabel?.replace('tenant:', '') || null,
        tenant_name: null,
        tenant_id: null,
        reporter_username: null,
        reported_at: issue.created_at,
        updated_at: issue.updated_at,
        _source: 'github',
      };
    });
  } catch {
    return null;
  }
}

/**
 * Close a bug issue from the super-admin portal.
 * Calls the close-bug-issue Edge Function which posts an optional comment,
 * closes the GitHub issue, and updates bug_refs status to "deployed".
 *
 */
export async function closeBugIssue(issueNumber, note = '') {
  const headers = { 'Content-Type': 'application/json' };

  const token = getSessionToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(getFunctionUrl('close-bug-issue'), {
    method: 'POST',
    headers,
    body: JSON.stringify({ issue_number: issueNumber, note: note.trim() }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Failed to close issue' }));
    throw new Error(err.error || 'Failed to close issue');
  }

  return response.json();
}

/**
 * Detect the current environment from the hostname.
 */
function detectEnvironment() {
  const hostname = window.location.hostname.toLowerCase();
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'development';
  if (hostname.includes('dev.environment.')) return 'development';
  if (hostname.includes('test.environment.')) return 'test';
  return 'production';
}
